import { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { formatCurrency } from '../utils/currency';

// ─── Stripe CardElement visual style ─────────────────────────────────────────
const CARD_STYLE = {
  style: {
    base: {
      fontSize: '16px',
      color: '#1a1a2e',
      fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
      fontSmoothing: 'antialiased',
      '::placeholder': { color: '#9ca3af' },
    },
    invalid: {
      color: '#dc2626',
      iconColor: '#dc2626',
    },
  },
};

// ─── PaymentForm ──────────────────────────────────────────────────────────────
// Must be rendered inside an <Elements stripe={...}> provider.
//
// Props:
//   clientSecret  – Stripe PaymentIntent client_secret
//   songs         – array of { id, title } objects being paid for
//   amount        – total in cents
//   feePerSong    – fee per song in cents (for display)
//   isMember      – boolean, drives the membership label
//   onSuccess     – callback(paymentIntentId: string)
// ---------------------------------------------------------------------------
export default function PaymentForm({
  clientSecret,
  songs,
  amount,
  feePerSong,
  isMember,
  onSuccess,
}) {
  const stripe   = useStripe();
  const elements = useElements();

  const [processing, setProcessing] = useState(false);
  const [cardError, setCardError]   = useState('');

  async function handlePay(e) {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setCardError('');

    const cardElement = elements.getElement(CardElement);

    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: { card: cardElement },
    });

    if (error) {
      setCardError(error.message);
      setProcessing(false);
      return;
    }

    if (paymentIntent.status === 'succeeded') {
      onSuccess(paymentIntent.id);
    } else {
      setCardError(`Unexpected payment status: ${paymentIntent.status}. Please try again.`);
      setProcessing(false);
    }
  }

  return (
    <div className="c-form-card">
      <h3 className="c-form-card__title">💳 Entry Fee Payment</h3>

      {/* ── Fee summary ── */}
      <div className="c-payment-summary">
        <div className="c-payment-summary__badge">
          {isMember ? '✓ SCALA Member rate' : 'Non-member rate'}
        </div>

        <ul className="c-payment-summary__list">
          {songs.map((song, i) => (
            <li key={song.id ?? i} className="c-payment-summary__item">
              <span className="c-payment-summary__item-title">
                🎵 {song.title}
              </span>
              <span className="c-payment-summary__item-price">
                {formatCurrency(feePerSong)}
              </span>
            </li>
          ))}
        </ul>

        <div className="c-payment-summary__total">
          <span>Total</span>
          <span>{formatCurrency(amount)}</span>
        </div>
      </div>

      {/* ── Card input ── */}
      <form onSubmit={handlePay}>
        <div className="c-form-group">
          <label className="c-label">Card details</label>
          <div className="c-card-element">
            <CardElement
              options={CARD_STYLE}
              onChange={() => setCardError('')}
            />
          </div>
        </div>

        {cardError && (
          <div className="c-alert" role="alert">{cardError}</div>
        )}

        <button
          type="submit"
          className="c-submit-btn"
          disabled={!stripe || processing}
        >
          {processing ? (
            <>
              <span
                className="c-spinner c-spinner--sm c-spinner--white"
                aria-hidden="true"
              />
              Processing…
            </>
          ) : (
            `Pay ${formatCurrency(amount)}`
          )}
        </button>

        <p className="c-payment-test-note">       
        </p>
      </form>
    </div>
  );
}
